import { Link, Navigate, Outlet } from "react-router-dom"

/** Global toasts: `<ToastProvider />` is mounted in `main.tsx` (Sonner, top-right). */
import { Sidebar } from "@/components/shell/sidebar"
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar"
import { EmailVerificationBanner } from "@/components/shell/email-verification-banner"
import { Button } from "@/components/ui/button"
import { LogoIcon } from "@/components/logo"
import { useOnboardingProfile } from "@/hooks/use-onboarding"
import { useAuth } from "@/lib/auth"
import { APP_NAME_TITLE } from "@/lib/app-brand"
import { icon, statusBg, statusText } from "@/lib/design-tokens"
import { cn } from "@/lib/utils"

function MobileBanner() {
  return (
    <div className="shrink-0 border-b md:hidden">
      <div className="flex items-center gap-2 bg-background px-3 py-2">
        <SidebarTrigger className="-ml-0.5 shrink-0" />
        <Link
          to="/dashboard"
          className="flex min-w-0 flex-1 items-center gap-2 font-semibold tracking-tight"
        >
          <LogoIcon className={cn(icon.lg, "shrink-0 text-primary")} aria-hidden />
          <span className="truncate">{APP_NAME_TITLE}</span>
        </Link>
      </div>
      <p
        className={cn(
          "px-3 pb-2 text-center text-xs",
          statusBg.warning,
          statusText.warning,
        )}
      >
        {APP_NAME_TITLE} works best on desktop
      </p>
    </div>
  )
}

function ShellLayoutInner() {
  const { user, signOut } = useAuth()
  const { needsOnboarding, loading: profileLoading, error: profileError, refresh } =
    useOnboardingProfile()

  if (profileLoading) {
    return (
      <div
        className="flex min-h-svh items-center justify-center bg-background p-4 text-sm text-muted-foreground"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        Loading…
      </div>
    )
  }

  if (user && profileError) {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center gap-4 bg-background p-6 text-center">
        <p className="max-w-md text-sm text-destructive">{profileError}</p>
        <p className="max-w-md text-xs text-muted-foreground">
          We could not verify your account against{" "}
          <span className="font-mono">profiles</span>. This is usually Row Level
          Security or a missing profile row after sign-up.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button type="button" onClick={() => void refresh()}>
            Try again
          </Button>
          <Button type="button" variant="outline" onClick={() => void signOut()}>
            Sign out
          </Button>
        </div>
      </div>
    )
  }

  if (needsOnboarding) {
    return <Navigate to="/onboarding" replace />
  }

  return (
    <SidebarProvider>
      <div className="flex h-svh w-full bg-background text-foreground">
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-3 focus:left-3 focus:z-50 focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-sm focus:text-primary-foreground"
        >
          Skip to main content
        </a>

        <Sidebar />

        <SidebarInset
          id="main-content"
          tabIndex={-1}
          className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden focus:outline-none"
        >
          {/* Mobile top bar (includes sidebar sheet trigger) */}
          <div className="shrink-0">
            <MobileBanner />
          </div>

          {/* Email verification banner */}
          <EmailVerificationBanner />

          <div className="relative isolate flex min-h-0 flex-1 flex-col overflow-hidden">
            <Outlet />
          </div>
        </SidebarInset>
      </div>
    </SidebarProvider>
  )
}

export function ShellLayout() {
  return <ShellLayoutInner />
}
